import { useMemo } from 'react';

import type { AutocompleteSuggestion } from '@klareospace/pharos-lib-types';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useApiClient } from '../api';
import { useDebouncedValue } from '../hooks/use-debounced-value';
import { GEO_API_CLIENT_NAME, SEARCH_DEBOUNCE_MS } from '../constants';
import { DEFAULT_MAP_CENTER } from './default-map-center';
import { createGeocodingApi } from './geocoding-api';

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_AT = `${DEFAULT_MAP_CENTER.lat},${DEFAULT_MAP_CENTER.lng}`;
const MIN_QUERY_LENGTH = 3;
const STALE_TIME_MS = 60_000;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseAutocompleteOptions {
  at?: string;
  enabled?: boolean;
  query: string;
}

export interface UseAutocompleteReturn {
  error: Error | null;
  isLoading: boolean;
  suggestions: AutocompleteSuggestion[];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAutocomplete({
  at = DEFAULT_AT,
  enabled = true,
  query,
}: UseAutocompleteOptions): UseAutocompleteReturn {
  const { i18n } = useTranslation();
  const client = useApiClient(GEO_API_CLIENT_NAME);
  const api = useMemo(() => createGeocodingApi(client), [client]);

  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);

  const result: UseQueryResult<AutocompleteSuggestion[]> = useQuery({
    enabled: enabled && debouncedQuery.length >= MIN_QUERY_LENGTH,
    queryFn: () => api.autocomplete({ at, lang: i18n.language, query: debouncedQuery }).then((r) => r.suggestions),
    // `at` and `lang` must be part of the key — a language or viewpoint change must refetch
    // instead of silently serving a cached result from the previous language/location.
    queryKey: ['autocomplete', debouncedQuery, at, i18n.language],
    staleTime: STALE_TIME_MS,
  });

  return {
    error: result.error,
    isLoading: result.isLoading,
    suggestions: result.data ?? [],
  };
}
